import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { API, endpoints } from 'src/api';
import { Workspace } from 'src/components/sidebar/store';
import { FileType as BaseFileType } from 'src/features/files/types/file';
import useSWR from 'swr';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface UseFilesProps {
  filter: 'active' | 'archived' | 'deleted';
  itemLimit: number;
  mode: string;
  sortValue: 'dsc' | 'asc' | 'last' | 'recent';
  pageNum: number;
  workspace: string;
  search?: string;
}

export interface useFilesReturnType<DataType> {
  data: DataType;
  error: unknown;
  isLoading: boolean;
  total: number;
  revalidate: () => Promise<boolean>;
  pinFile?: (file: any, pin: boolean) => void;
}

export interface FileType {
  createdAt: string;
  id: string;
  name: string;
  title: string;
  updatedAt: string;
  shared: any[];
  columns?: any[];
  icon?: any;
  _id?: string;
  workspace: Workspace;
}

export const useFiles = ({
  filter,
  itemLimit,
  mode,
  pageNum,
  sortValue,
  workspace,
  search,
}: UseFilesProps): useFilesReturnType<FileType[]> => {
  endpoints.files.filter = filter;
  endpoints.files.itemLimit = itemLimit;
  endpoints.files.mode = mode;
  endpoints.files.page = pageNum;
  endpoints.files.sort = sortValue;
  endpoints.files.workspace = workspace;
  endpoints.files.search = search || '';

  const { data, error, revalidate } = useSWR(endpoints.files.url, API.get);
  const pinFile = (file, pin) => {
    const fileToPin = data?.data.data.files.find(
      (f) => f.id === file.id ?? file._id ?? file.fileId
    );
    fileToPin && ((fileToPin.pinned = pin), (fileToPin.pin = pin));
  };

  return {
    data: data?.data?.data?.files || [],
    total: data?.data?.data?.totalFiles || 0,
    error,
    isLoading: !data && !error,
    revalidate,
    pinFile,
  };
};

export const useFilesWithBoards = ({
  filter,
  itemLimit,
  mode,
  pageNum,
  sortValue,
  workspace,
}: UseFilesProps): useFilesReturnType<FileType[]> => {
  endpoints.files.filter = filter;
  endpoints.files.itemLimit = itemLimit;
  endpoints.files.mode = mode;
  endpoints.files.page = pageNum;
  endpoints.files.sort = sortValue;
  endpoints.files.workspace = workspace;

  const { data, error, revalidate } = useSWR(endpoints.boardFiles.url, API.get);

  return {
    data: data?.data?.data?.files || [],
    total: data?.data?.data?.totalFiles || 0,
    error,
    isLoading: !data && !error,
    revalidate,
  };
};

export interface FilesState {
  data: BaseFileType[];
  total: number;
  error: unknown;
  isLoading: boolean;
  actions: {
    fetchFiles: ({
      filter,
      itemLimit,
      mode,
      pageNum,
      sortValue,
      workspace,
      search,
    }: {
      filter: any;
      itemLimit: number;
      mode: string;
      pageNum: number;
      sortValue: string;
      workspace: string;
      search: string;
    }) => void;
    pinFile?: (file: any, pin: boolean) => void;
  };
}

const getInitialValue = () => ({
  data: [],
  total: 0,
  error: undefined,
  isLoading: false,
});

export const useFilesStore = create<
  FilesState,
  [['zustand/devtools', FilesState], ['zustand/immer', FilesState]]
>(
  devtools(
    zustandImmerMiddleware((set) => ({
      actions: {
        fetchFiles: async ({ filter, itemLimit, mode, pageNum, sortValue, workspace, search }) => {
          const queryParam = new URLSearchParams();

          filter && queryParam.set('filter', filter);
          queryParam.set('limit', `${itemLimit}` || '20');
          mode && queryParam.set('mode', mode);
          queryParam.set('page', `${pageNum}` || '0');
          sortValue && queryParam.set('sort', sortValue);
          workspace && queryParam.set('workspace', workspace);
          search && queryParam.set('search', search);

          const url = `/files?${queryParam}`;

          try {
            const data = await API.get(url);
            const files = data?.data?.data?.files.map((file) => ({
              ...file,
              id: file.id ?? file._id,
            }));
            if (files) {
              set((s) => {
                s.data = files || [];
                s.total = data?.data?.data?.totalFiles || 0;
                s.error = null;
                s.isLoading = !data && !s.error;
              });
            }
          } catch (error) {
            console.log(error);
          }
        },
        pinFile: (file: any, pin: boolean) => {
          set((s) => {
            const fileId = file.id ?? file._id ?? file.fileId;
            const fileToPin = s.data.find((f) => f.id === fileId);
            fileToPin && ((fileToPin.pinned = pin), (fileToPin.pin = pin));
          });
        },
      },

      ...getInitialValue(),
    }))
  )
);
